import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { AudienceContentSection } from '../components/AudienceContent';
import type { ClientType } from '../types/api';
import { ArrowLeft, Users, GraduationCap, Building2 } from 'lucide-react';

const AUDIENCE_ICONS: Record<ClientType, React.ComponentType<{ className?: string }>> = {
  individual: Users,
  student: GraduationCap,
  organization: Building2,
};

export function AudiencePage() {
  const { t } = useTranslation('audience');
  const { type } = useParams<{ type: string }>();

  // Validate audience type
  const isValidAudience = (type: string): type is ClientType => {
    return ['individual', 'student', 'organization'].includes(type);
  };

  if (!type || !isValidAudience(type)) {
    return (
      <div className="min-h-screen bg-dark pt-16">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-text-primary mb-4 font-heading">{t('notFound.title')}</h1>
          <p className="text-text-secondary mb-8">
            {t('notFound.description')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center text-neon-cyan hover:text-neon-purple font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('notFound.back')}
          </Link>
        </main>
      </div>
    );
  }

  // SEO data for each audience type
  const audienceSEO: Record<ClientType, { title: string; description: string; keywords: string }> = {
    individual: {
      title: 'บริการสำหรับบุคคลทั่วไป',
      description: 'โซลูชันดิจิทัลสำหรับผู้ประกอบการรายย่อยและฟรีแลนซ์ พัฒนาเว็บและแอปตามงบประมาณ เหมาะกับ SME และ startups',
      keywords: 'บุคคลทั่วไป, ฟรีแลนซ์, SME, startups, individual, entrepreneur'
    },
    student: {
      title: 'บริการสำหรับนักศึกษา',
      description: 'ราคาพิเศษสำหรับนักศึกษา พร้อมเรียนรู้เทคโนโลยีใหม่ๆ และสร้างพอร์ตโฟลิโอ รองรับโปรเจกต์จบ internship',
      keywords: 'นักศึกษา, ราคาพิเศษ, student, discount, internship, portfolio'
    },
    organization: {
      title: 'บริการสำหรับองค์กร',
      description: 'โซลูชันดิจิทัลระดับองค์กร พัฒนาระบบ enterprise พร้อมการสนับสนุนแบบเต็มรูปแบบ เหมาะกับธุรกิจขนาดกลางและขนาดใหญ่',
      keywords: 'องค์กร, enterprise, B2B, corporate, organization, business'
    }
  };

  const seoData = audienceSEO[type];

  return (
    <div className="min-h-screen bg-dark">
      <SEO
        title={seoData.title}
        description={seoData.description}
        url={`https://www.happytech.dev/audience/${type}`}
        keywords={seoData.keywords}
      />
      <Header />

      <main className="pt-16">
        {/* Back button */}
        <div className="border-b border-white/5 bg-dark-100/50 backdrop-blur-sm">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-4">
            <Link
              to="/"
              className="inline-flex items-center text-text-secondary hover:text-neon-cyan transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('notFound.back')}
            </Link>
          </div>
        </div>

        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-neon-cyan/20 to-neon-purple/20 py-16">
          {/* Background orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="orb absolute top-0 left-1/4 w-96 h-96 bg-neon-cyan/10 rounded-full blur-3xl" />
            <div className="orb-delayed absolute bottom-0 right-1/4 w-96 h-96 bg-neon-purple/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading">
              <span className="gradient-text">{t(`${type}.title`)}</span>
            </h1>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              {t(`${type}.description`)}
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <AudienceContentSection type={type} />
        </div>

        {/* Other Audiences CTA */}
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 pb-16">
          <div className="glass-card rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-text-primary mb-4 text-center font-heading">
              {t('section.exploreOthers')}
            </h2>
            <p className="text-text-secondary text-center mb-6">
              {t('section.notSure')}
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {(['individual', 'student', 'organization'] as ClientType[])
                .filter((key) => key !== type)
                .map((key) => {
                  const Icon = AUDIENCE_ICONS[key];
                  return (
                    <Link
                      key={key}
                      to={`/audience/${key}`}
                      className="flex items-center gap-4 p-4 rounded-xl glass-card hover:bg-white/[0.07] hover:border-neon-cyan/30 transition-all group"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-neon-cyan/10 flex items-center justify-center group-hover:bg-neon-cyan/20 transition-colors">
                        <Icon className="w-5 h-5 text-neon-cyan" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-text-primary group-hover:text-neon-cyan transition-colors font-heading">
                          {t(`${key}.title`)}
                        </h3>
                        <p className="text-sm text-text-secondary line-clamp-1">{t(`${key}.description`)}</p>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
